<template>
  <div
    class="container apartment-search-form"
    :class="classSize"
  >
    <div class="apartment-search-form__grid">

      <!-- Sidebar -->
      <div class="apartment-search-form__sidebar">
        <div class="apartment-search-form__title">
          {{ form.type == 'right-of-occupancy' ? translate('Löydä uusi kotisi') :
               form.type == 'rental' ? translate('RENT: ohje otsikko') :
               translate('OWN: ohje otsikko')
          }}
        </div>

        <div class="apartment-search-form__instructions">
          <div class="apartment-search-form__instructions-unit">
            <div class="apartment-search-form__instructions-circle-of-step">1</div>
            <div class="apartment-search-form__instructions-text">
              {{ form.type == 'right-of-occupancy' ? translate('Hae asuntoja') :
                   form.type == 'rental' ? translate('RENT: ohje 1') :
                   translate('OWN: ohje 1')
              }}
            </div>
          </div>
          <div class="apartment-search-form__instructions-unit">
            <div class="apartment-search-form__instructions-circle-of-step">2</div>
            <div class="apartment-search-form__instructions-text">
              {{ form.type == 'right-of-occupancy' ? translate('Lisää mieleisesi asunnot hakemukseen') :
                   form.type == 'rental' ? translate('RENT: ohje 2') :
                   translate('OWN: ohje 2')
              }}
            </div>
          </div>
          <div class="apartment-search-form__instructions-unit">
            <div class="apartment-search-form__instructions-circle-of-step">3</div>
            <div class="apartment-search-form__instructions-text">
              {{ form.type == 'right-of-occupancy' ? translate('Täytä ja lähetä asuntohakemus') :
                   form.type == 'rental' ? translate('RENT: ohje 3') :
                   translate('OWN: ohje 3')
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- Main -->
      <form
        class="apartment-search-form__main"
        @submit.prevent="submit"
        id="apartment-search-form-1001"
      >
        <ul class="apartment-search-form__main-tabs">
          <li
            v-for="tab in tabs"
            class="apartment-search-form__tab"
          >
            <button
              v-if="showSearchFormTab[tab.value]"
              type="button"
              :class="classTab(tab)"
              @click="form.type = tab.value"
            >
              {{translate(tab.title)}}
            </button>
          </li>
        </ul>
        <div class="apartment-search-form__main-top">
          <div class="apartment-search-form__main-top-column-main">
            <!-- Location -->
            <div class="apartment-search-form__location">
              <InputWrap
                :label="translate('Sijainti')"
                :wrap-label="false"
                v-slot="{id, error}"
              >
                <InputSelect
                  :id="id"
                  :options="locationOptions"
                  :internal-search="false"
                  :placeholder="translate('Paikkakunta, kaupunginosa, katu tai postinumero')"
                  :close-on-select="false"
                  track-by="value"
                  label="name"
                  data-cy="select-city"
                  @search-change="onLocationSearchTextChange"
                  @select="form.locations.push({...$event})"
                  hide-caret
                  :hide-dropdown="locationSearchText === null || locationSearchText.length < 2"
                  :error="error"
                  open-direction="bottom"
                >
                  <template #beforeList>
                    <div
                      v-if="locationOptionsLoading"
                      class="apartment-search-form__location-dropdown-loading"
                      :class="`apartment-search-form__location-dropdown-loading--${color}`"
                    >
                      <LoadSpinner :margin="false"/>
                    </div>
                    <div
                      class="apartment-search-form__location-dropdown-empty"
                      v-else-if="locationOptions.length === 0"
                    >
                      {{translate('Haulla ei löytynyt tuloksia.')}}
                    </div>
                  </template>
                </InputSelect>
              </InputWrap>

              <div
                v-if="form.locations.length > 0"
                class="apartment-search-form__location-tags"
              >
                <TagList
                  data-cy="city-tag-list"
                  :items="form.locations.map((location) => ({title: location.name, active: true}))"
                  :color="color"
                  @item-click="form.locations.splice($event, 1)"
                />
              </div>
            </div>

            <!-- Area & price -->
            <div :class="`apartment-search-form__double-field apartment-search-form__double-field--${color}--first`">
              <div class="apartment-search-form__double-field-column">
                <InputWrap :label="translate('Min pinta-ala')">
                  <InputText
                    v-model.number="form.minArea"
                    data-cy="min-area"
                    type="number"
                  >m²
                  </InputText>
                </InputWrap>
              </div>
              <div class="apartment-search-form__double-field-column" v-if="form.type !== 'owner-occupancy'">
                <InputWrap :label="translate(maxPriceTitle)">
                  <InputText
                    v-model.number="form.maxPrice"
                    data-cy="max-price"
                    type="number"
                  >€
                  </InputText>
                </InputWrap>
              </div>
            </div>

            <!-- min/max debt free price -->
            <div v-if="form.type === 'owner-occupancy'" :class="`apartment-search-form__double-field apartment-search-form__double-field--${color}--second`">
              <div class="apartment-search-form__double-field-column">
                <InputWrap :label="translate('Min velaton hinta')">
                  <InputText
                    v-model.number="form.minDebtFreePrice"
                    data-cy="min-debt-free-price"
                    type="number"
                  >€
                  </InputText>
                </InputWrap>
              </div>
              <div class="apartment-search-form__double-field-column">
                <InputWrap :label="translate('Max velaton hinta')">
                    <InputText
                      v-model.number="form.maxDebtFreePrice"
                      data-cy="max-debt-free-price"
                      type="number"
                    >€
                    </InputText>
                  </InputWrap>
              </div>
            </div>

            <InputWrap
              v-if="form.type === 'right-of-occupancy'"
              :label="translate('Max luovutushinta')"
              class="apartment-search-form__total-price"
            >
              <InputText
                v-model.number="form.maxTotalPrice"
                type="number"
              >€
              </InputText>
            </InputWrap>
            <div class="apartment-search-form__campaign-checkbox" v-if="hasAnyCampaigns[form.type]">
                  <InputCheckbox
                    value="campaign"
                    :modelValue="form.booleanFilters.includes('campaign') || show_campaign === true"
                    @change="toggleBooleanFilter('campaign')"
                  >
                    {{ translate('Vain kampanja-asunnot') }}
                  </InputCheckbox>
              </div>

          </div>

          <div class="apartment-search-form__main-top-column-side">

            <div class="apartment-search-form__grid-checkboxes">
              <!-- Room count -->
              <div class="apartment-search-form__grid-checkboxes-column">
                <InputWrap
                  :label="translate('Huoneita')"
                  data-cy="rooms"
                  :wrap-label="false"
                >
                  <div class="apartment-search-form__checkboxes">
                    <InputCheckbox
                      autocomplete="off"
                      v-for="count in roomCounts"
                      :key="count.value"
                      :value="count.value"
                      v-model="form.roomCounts"
                      class="apartment-search-form__checkbox"
                    >
                      {{count.label}}
                    </InputCheckbox>
                  </div>
                </InputWrap>
              </div>

              <!-- Building type -->
              <div class="apartment-search-form__grid-checkboxes-column">
                <InputWrap
                  :label="translate('Talotyyppi')"
                  data-cy="building-type"
                  :wrap-label="false"
                >
                  <div class="apartment-search-form__checkboxes">
                    <InputCheckbox
                      autocomplete="off"
                      v-for="type in buildingTypes[form.type]"
                      :key="type.value"
                      :value="type.value"
                      v-model="form.buildingTypes"
                      class="apartment-search-form__checkbox"
                    >
                      {{translate(type.label)}}
                    </InputCheckbox>
                  </div>
                </InputWrap>
              </div>
            </div>

            <div class="apartment-search-form__showing-checkbox">
              <InputCheckbox
                value="showing"
                autocomplete="off"
                :modelValue="form.booleanFilters.includes('showing')"
                @change="toggleBooleanFilter('showing')"
              >
                {{translate('Vain esittelyssä olevat asunnot')}}
              </InputCheckbox>
            </div>

          </div>
        </div>

        <div class="apartment-search-form__main-middle" v-if="form.type !== 'owner-occupancy'">
          <div class="apartment-search-form__toggle-more-container">
            <button
              type="button"
              class="apartment-search-form__toggle-more"
              @click="showAll = !showAll"
            >
            <MinusIcon
              v-if="showAll"
              class="apartment-search-form__toggle-more-icon"
              :class="`apartment-search-form__toggle-more-icon--${color}`"/>
            <PlusIcon
              v-else
              class="apartment-search-form__toggle-more-icon"
              :class="`apartment-search-form__toggle-more-icon--${color}`"/>
              {{showAll ? translate('Piilota hakuehdot') : translate('Lisää hakuehtoja')}}
            </button>
          </div>

          <div
            v-if="showAll"
            class="apartment-search-form__hidden-fields"
          >
            <InputWrap
              :label="translate('Ominaisuudet')"
              :wrap-label="false"
            >
              <TagList
                :items="drawerBooleanFilterOptions"
                :color="color"
                class="apartment-search-form__boolean-filters"
                @item-click="onDrawerBooleanFilterClick($event)"
              />
            </InputWrap>

            <div class="apartment-search-form__bottom-filters-title">
              {{translate('Rajausvaihtoehdot')}}
            </div>
            <div class="apartment-search-form__bottom-filters">
              <InputWrapDate
                v-model="form.availableBefore"
                class="apartment-search-form__bottom-filter-col"
                :label="translate('Vapautumispäivä xx.xx. mennessä')"
              />
              <div class="apartment-search-form__bottom-filter-col">
                <InputWrap
                  :label="translate('Rakennusvuosi min-max')"
                  :wrap-label="false"
                >
                  <div class="apartment-search-form__joined-fields">
                    <InputSelect
                      class="apartment-search-form__joined-field"
                      :options="optionsYearMin"
                      v-model="form.yearMin"
                      simple-value
                    />
                    <InputSelect
                      class="apartment-search-form__joined-field"
                      :options="optionsYearMax"
                      v-model="form.yearMax"
                      simple-value
                    />
                  </div>
                </InputWrap>
              </div>
            </div>
          </div>
        </div>

        <div class="apartment-search-form__main-bottom">
          <button
            type="submit"
            class="btn btn--large"
            :class="`btn--${color}`"
            id="apartment-search-form-button-2001"
          >
            {{translate('Hae asuntoja')}}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import {debounce} from 'debounce';
  import { useVuelidate } from '@vuelidate/core';
  import globals from '../../lib/globals';
  import utils from '../mixins/utils';
  import InputWrap from './InputWrap';
  import InputText from './InputText';
  import TagList from './TagList';
  import InputCheckbox from './InputCheckbox';
  import InputSelect from './InputSelect';
  import InputWrapDate from './InputWrapDate';
  import LoadSpinner from './LoadSpinner';
  import MinusIcon from '../icons/MinusIcon.vue';
  import PlusIcon from '../icons/PlusIcon.vue';

  export default {
    components: {
      LoadSpinner,
      InputCheckbox,
      TagList,
      InputWrap,
      InputText,
      InputSelect,
      InputWrapDate,
      MinusIcon,
      PlusIcon
    },
    mixins: [
      utils.translate,
    ],
    setup() {
      return { v$: useVuelidate() };
    },
    validations() {
      return {};
    },
    props: {
      preset: {
        type: Object,
        default: null,
      },
      size: {
        type: String,
        default: 'full',
      },
      show_campaign: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      const preset = this.preset ? this.preset : {};
      const show_campaign = this.show_campaign ? this.show_campaign : false;
      const form = {
        type: 'right-of-occupancy',
        locations: [],
        minArea: '',
        maxPrice: '',
        maxTotalPrice: '',
        minDebtFreePrice: '',
        maxDebtFreePrice: '',
        availableBefore: null,
        roomCounts: [],
        buildingTypes: [],
        booleanFilters: [],
        yearMin: null,
        yearMax: null,
        ...preset,
      };

      const yearNow = new Date().getFullYear();
      const optionsYearBase = [...Array(150).keys()].map(i => {
        const year = yearNow + 1 - i;
        return {
          label: year,
          value: year,
        };
      });

      const drawerBooleanFilterOptionsBase = [
        {
          title: 'Heti vapaa',
          value: 'available',
          type: 'all',
        },
        {
          title: 'Vain uudiskohteet',
          value: 'new',
          type: 'all',
        },
        {
          title: 'Huoneistosauna',
          value: 'sauna',
          type: 'all',
        },
        {
          title: 'Hissi',
          value: 'elevator',
          type: 'all',
        },
        {
          title: 'Talosauna',
          value: 'housing_locality_sauna',
          type: 'all',
        },
        {
          title: 'Pesutupa',
          value: 'laundry_room',
          type: 'all',
        },
        {
          title: 'Savuton kohde',
          value: 'non_smoking',
          type: 'all',
        },
        {
          title: 'Parveke',
          value: 'balcony',
          type: 'all',
        },
      ];

      const activeDrawerBooleanFilters = form.booleanFilters
        .filter(filter => drawerBooleanFilterOptionsBase.map(f => f.value).includes(filter));
      return {
        optionsYearBase,
        locationSearchText: null,
        locationOptionsLoading: false,
        locationOptions: [],
        hasAnyCampaigns: {},
        showSearchFormTab: {'right-of-occupancy': true, 'rental': true}, // show roo and rental by default
        roomCounts: [...[...Array(4).keys()]
          .map((index) => ({
            label: (index + 1).toString(),
            value: index + 1,
          })), {
          label: '5+',
          value: 5,
        }],
        buildingTypes: {
          'right-of-occupancy': []
            /*[
              'Erillistalo',
              'Luhtitalo',
              'Kerrostalo',
              'Rivitalo',
              'Paritalo',
            ].map((type) => ({
              label: type,
              value: type,
            }))*/,
          'rental':
            [
/*              {label: 'test', value: 'test'},
              {label: 'Kerrostalo', value: 'Kerrostalo'}*/
            ],
          'owner-occupancy': [],
        },
        tabs: [
          {
            title: 'ASO-asunnot',
            value: 'right-of-occupancy',
            color: 'green',
          },
          {
            title: 'Vuokra-asunnot',
            value: 'rental',
            color: 'green',
          },
          {
            title: 'Omistusasunnot',
            value: 'owner-occupancy',
            color: 'green',
          }
        ],
        drawerBooleanFilterOptionsBase,
        showAll: (
          activeDrawerBooleanFilters.length > 0
          ||
          form.availableBefore
          ||
          form.yearMax
          ||
          form.yearMin
          ||
          form.minDebtFreePrice
          ||
          form.maxDebtFreePrice
        ),
        form,
      };
    },
    mounted() {
      this.hasAnyCampaignsRequest();
      this.checkApartments();
      this.getBuildingTypes();

      if (this.show_campaign) {
        this.toggleBooleanFilter('campaign');
      }
    },
    computed: {
      drawerBooleanFilterOptions() {
        return this.drawerBooleanFilterOptionsBase.map((option) => ({
          ...option,
          title: this.translate(option.title),
          active: this.form.booleanFilters.includes(option.value),
        }));
      },
      maxPriceTitle() {
        return {
          rental: 'Max vuokra',
          'right-of-occupancy': 'Max vastike',
          'owner-occupancy': 'Max vastike',
        }[this.form.type];
      },
      optionsYearMin() {
        if (!this.form.yearMax) {
          return this.optionsYearBase;
        }
        const indexMax = this.optionsYearBase.findIndex(option => option.value === this.form.yearMax);
        return this.optionsYearBase.map((option, index) => ({
          ...option,
          $isDisabled: index < indexMax,
        }));
      },
      optionsYearMax() {
        if (!this.form.yearMin) {
          return this.optionsYearBase;
        }
        const indexMin = this.optionsYearBase.findIndex(option => option.value === this.form.yearMin);
        return this.optionsYearBase.map((option, index) => ({
          ...option,
          $isDisabled: index > indexMin,
        }));
      },
      classSize() {
        return {
          'apartment-search-form--card-wide': this.size === 'card-wide',
          'apartment-search-form--full': this.size === 'full',
        };
      },
      color() {
        return this.tabs.find((tab) => tab.value === this.form.type).color;
      },
    },
    methods: {
      onLocationSearchTextChange(searchText) {
        this.locationOptions = [];
        this.locationSearchText = searchText;

        if (searchText.length < 2) {
          return;
        }

        this.locationOptionsLoading = true;
        this.requestLocationsLazy(searchText);
      },
      requestLocationsLazy: debounce(async function (searchText) {
        let searchType = this.form.type;
        const url = `${globals.urlIntegrationApi}/public/location-filters-search?s=${encodeURIComponent(searchText)}&t=${searchType}`;
        const response = await axios.get(url);

        // Bail out if the search text has been changed since
        if (this.locationSearchText !== searchText) {
          return;
        }

        this.locationOptionsLoading = false;
        this.locationOptions = response.data
          .filter((item) => !this.form.locations.map(i => i.value).includes(item.value))

      }, 500),
      checkApartments: debounce(async function() {
        // Sets housing types which have apartments (right-of-occupancy, rental, owner-occupancy)
        const url = `${globals.urlIntegrationApi}/public/apartment-check`;
        const response = await axios.get(url);
        if(response.status != 200) {
          return false;
        }

        if(!response.hasOwnProperty('data') || response.data == null || response.data.length <= 0 || !response.data.hasOwnProperty('apartmentsFoundForType')) {
          return false;
        }

        this.showSearchFormTab = response.data.apartmentsFoundForType;
        return true;
      }, 100),
      getBuildingTypes: debounce(async function() {
        // Get building types for different habitation modes (right-of-occupancy, rental, owner-occupancy)
        const url = `${globals.urlIntegrationApi}/public/building-types`;
        const response = await axios.get(url);
        if(response.status != 200) {
          return false;
        }

        if(!response.hasOwnProperty('data') || response.data == null || response.data.length <= 0 || !response.data.hasOwnProperty('buildingTypes')) {
          return false;
        }

        const buildingTypesTmp = response.data.buildingTypes;
        for (var searchType in buildingTypesTmp) {
          this.buildingTypes[searchType] = buildingTypesTmp[searchType].map((type) => ({
            label: type,
            value: type,
          }));
        }

        return true;
      }, 100),
      hasAnyCampaignsRequest: debounce(async function () {
        const url = `${globals.urlIntegrationApi}/public/campaign-check`;
        const response = await axios.get(url);
        if(response.status != 200) {
          return false;
        }

        if(!response.hasOwnProperty('data') || response.data == null || response.data.length <= 0 || !response.data.hasOwnProperty('campaignApartmentsFound')) {
          return false;
        }

        this.hasAnyCampaigns = response.data.campaignApartmentsFound;
      }, 100),
      onDrawerBooleanFilterClick(index) {
        const filterClicked = this.drawerBooleanFilterOptions[index].value;
        this.toggleBooleanFilter(filterClicked);
      },
      toggleBooleanFilter(filter) {
        const filterIndex = this.form.booleanFilters.findIndex(f => f === filter);
        if (filterIndex === -1) {
          this.form.booleanFilters.push(filter);
        } else {
          this.form.booleanFilters.splice(filterIndex, 1);
        }
      },
      submit() {
        const formCopy = JSON.parse(JSON.stringify(this.form));

        const stateString = encodeURIComponent(JSON.stringify(formCopy));
        this.$emit('submit', formCopy, stateString);
        window.history.pushState('', '', `?q=${stateString}`);
      },
      classTab(tab) {
        return [
          'apartment-search-form__tab-button',
          `apartment-search-form__tab-button--${this.color}`,
          {'apartment-search-form__tab-button--active': this.form.type === tab.value}
        ];
      }
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  @mixin apartment-search-form__medium {
    .apartment-search-form--card-wide & {
      @include media-breakpoint-up(xl) {
        @content;
      }
    }

    .apartment-search-form--full & {
      @include media-breakpoint-up(md) {
        @content;
      }
    }
  }

  @mixin apartment-search-form__large {
    .apartment-search-form--full & {
      @include media-breakpoint-up(xl) {
        @content;
      }
    }
  }

  @mixin apartment-search-form__main-top-column() {
    margin-bottom: r(16);

    @include apartment-search-form__large {
      margin: 0 r(24);
    }
  }

  .apartment-search-form {
    @include card;
    background-color: $color-white;
  }

  .apartment-search-form__grid {
    padding: r(40) r(12);

    @include apartment-search-form__medium {
      display: flex;
      padding: 0 r(32);
    }

    @include apartment-search-form__large {
      padding: 0 r(80) 0 r(48);
    }
  }

  .apartment-search-form__sidebar {
    margin-bottom: r(24);

    @include apartment-search-form__medium {
      width: r(160);
      padding-top: r(32);
      padding-right: r(24);
      border-right: r(1) solid #d8d8d8;
      margin-right: r(16);
      margin-bottom: 0;
    }

    @include apartment-search-form__large {
      width: r(280);
      padding-right: r(48);
      padding-top: r(40);
      margin-right: r(40);
    }
  }

  .apartment-search-form__title {
    @include font-h-28;
    margin-bottom: r(24);
    text-align: center;

    @include apartment-search-form__medium {
      margin-bottom: r(40);
      text-align: left;
    }

    @include apartment-search-form__large {
      @include font-h-42;
    }
  }

  .apartment-search-form__instructions {
    display: none;

    @include apartment-search-form__medium {
      display: block;
    }
  }

  .apartment-search-form__instructions-unit {
    @include font-label-16;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    @include apartment-search-form__large {
      flex-direction: row;
      text-align: left;
    }

    & + & {
      margin-top: r(22);
    }
  }

  .apartment-search-form__instructions-text {
    width: 100%; // ie
  }

  .apartment-search-form__instructions-icon {
    margin-bottom: r(8);

    @include apartment-search-form__large {
      margin-right: r(16);
      margin-bottom: 0;
    }

    svg {
      width: r(32);
      height: r(32);

      @include apartment-search-form__large {
        width: r(52);
        height: r(52);
      }
    }
  }

  .apartment-search-form__instructions-circle-of-step {
    display: flex;
    flex: 0 0 auto;
    width: r(32);
    height: r(32);
    justify-content: center;
    align-items: center;
    border: 2px solid currentColor;
    border-radius: 50%;
    margin-right: 0;
    margin-bottom: r(8);
    font-size: r(16);

    @include apartment-search-form__large {
      width: r(48);
      height: r(48);
      margin-right: r(16);
      margin-bottom: 0;
      font-size: r(20);
    }
  }

  .apartment-search-form__main {
    flex: 1;

    @include apartment-search-form__medium {
      padding: r(32) 0;
    }

    @include apartment-search-form__large {
      padding: r(40) 0;
    }
  }

  .apartment-search-form__main-tabs {
    @include reset-list;
    display: flex;
    justify-content: center;
    margin-bottom: r(24);

    @include apartment-search-form__medium {
      justify-content: flex-start;
      margin-bottom: r(40);
    }
  }

  .apartment-search-form__tab {
    & + & {
      margin-left: r(16);

      @include apartment-search-form__large {
        margin-left: r(28);
      }
    }
  }

  .apartment-search-form__tab-button {
    @include reset-button(true);
    @include font-h-16;
    position: relative;
    transition: padding-left 0.2s;
    color: rgba($color-black, 0.5);

    @include apartment-search-form__medium {
      @include font-h-18;
    }

    @include apartment-search-form__large {
      @include font-h-22;
    }

    &--active {
      color: $color-pink;

      &:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: r(-4);
        background-color: $color-pink;
        height: r(2);
      }
    }

    &--yellow.apartment-search-form__tab-button--active {
      color: $color-yellow;

      &:after {
        background-color: $color-yellow;
      }
    }

    &--blue.apartment-search-form__tab-button--active {
      color: $color-blue;

      &:after {
        background-color: $color-blue;
      }
    }
  }

  .apartment-search-form__main-top {
    @include apartment-search-form__medium {
      margin-bottom: r(48);
    }

    @include apartment-search-form__large {
      display: flex;
      justify-content: space-between;
    }
  }

  .apartment-search-form__main-top-column-main {
    @include apartment-search-form__main-top-column;
    margin-bottom: r(16);

    @include apartment-search-form__large {
      flex: 4 1;
      min-width: r(320);
      max-width: r(380);
    }
  }

  .apartment-search-form__location {
    margin-bottom: r(16);

    @include apartment-search-form__large {
      margin-bottom: r(40);
    }
  }

  .apartment-search-form__location-dropdown-loading {
    padding: r(12) 0;
    color: $color-pink;

    &--yellow {
      color: $color-yellow;
    }
  }

  .apartment-search-form__location-dropdown-empty {
    padding: r(8);
  }

  .apartment-search-form__location-tags {
    margin-top: r(10);
  }

  .apartment-search-form__double-field {
    display: flex;
    margin: 0 r(-10);
  }

   .apartment-search-form__double-field--blue--first {
    margin: 0 r(-10) r(16);
  }

  .apartment-search-form__double-field-column {
    width: 50%;
    padding: 0 r(10);
  }

  .apartment-search-form__total-price {
    margin-top: r(10);
  }

  .apartment-search-form__main-top-column-side {
    @include apartment-search-form__main-top-column;

    @include apartment-search-form__large {
      flex-grow: 1;
      max-width: r(264);
    }
  }

  .apartment-search-form__grid-checkboxes {
    @include apartment-search-form__large {
      display: flex;
      justify-content: space-between;
      margin-left: r(-16);
      margin-right: r(-16);
    }
  }

  .apartment-search-form__grid-checkboxes-column {
    margin-bottom: r(16);

    @include apartment-search-form__large {
      margin: 0 r(16);
    }
  }

  .apartment-search-form__checkboxes {
    display: flex;
    flex-wrap: wrap;
    margin: r(-4) r(-6);

    @include apartment-search-form__medium {
      margin: r(-7) r(-10);
    }

    @include apartment-search-form__large {
      padding-top: r(4);
      flex-direction: column;
      margin: 0 0 r(-8);
    }
  }

  .apartment-search-form__checkbox {
    margin: r(4) r(6);

    @include apartment-search-form__medium {
      margin: r(7) r(10);
    }

    @include apartment-search-form__large {
      margin: r(8) 0;
    }
  }

  .apartment-search-form__showing-checkbox {
    margin-top: r(16);
    border-top: r(1) solid #d8d8d8;
    padding-top: r(16);
  }
  .apartment-search-form__own-plot-checkbox {
    margin-top: r(16);
  }

  .apartment-search-form__campaign-checkbox {
    margin-top: r(16);
    font-weight: bold;
  }

  .apartment-search-form__campaign-checkbox {
    margin-top: r(16);
    font-weight: bold;
  }

  .apartment-search-form__main-middle {
    margin-bottom: r(16);

    @include apartment-search-form__medium {
      margin-bottom: r(4);
    }
  }

  .apartment-search-form__toggle-more-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    @include apartment-search-form__medium {
      justify-content: flex-start;
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      height: r(1);
      background-color: #d8d8d8;
    }
  }

  .apartment-search-form__toggle-more {
    @include reset-button(true);
    @include font-label-12;
    position: relative;
    display: flex;
    align-items: center;
    line-height: calc(15 / 12);
    padding: 0 r(8);
    background-color: $color-white;

    &:focus {
      text-decoration: underline;
    }

    @include apartment-search-form__large {
      @include font-label-14;
    }
  }

  .apartment-search-form__hidden-fields {
    margin-top: r(32);

    @include apartment-search-form__medium {
      margin-bottom: r(64);
    }
  }

  .apartment-search-form__boolean-filters {
    margin-top: r(16);
  }

  .apartment-search-form__bottom-filters-title {
    @include font-label-16;
    margin: r(40) 0 r(24);
  }

  .apartment-search-form__bottom-filters {
    margin: r(16) r(-8) r(-16);

    @include apartment-search-form__large {
      display: flex;
    }
  }

  .apartment-search-form__bottom-filter-col {
    padding: 0 r(8) r(16);

    @include apartment-search-form__large {
      width: 50%;
    }
  }

  .apartment-search-form__joined-fields {
    display: flex;
    margin: 0 r(-4);
  }

  .apartment-search-form__joined-field {
    width: 50%;
    margin: 0 r(4);
  }

  .apartment-search-form__main-bottom {
    display: flex;
    justify-content: center;

    @include apartment-search-form__medium {
      justify-content: flex-end;
    }
  }
</style>
